/**
 * The plan's third half: what an aircraft profile writes into the Formular.
 *
 * Same idea as `route.ts` — a pure mapping, no DOM, no network, no store — so
 * the rules can be pinned down by tests instead of by clicking. The store owns
 * *when* this runs and which cells it may touch (see `PlanStore.feedAircraft`);
 * this file only says what the numbers are.
 *
 * Two stages, because one of the values is derived from a value the calculator
 * itself produces:
 *   · {@link aircraftStaticValues} — everything that follows from the profile
 *     and the shape of the route (Muster, Kennzeichen, TAS per leg, tank
 *     figures, empty mass). Runs BEFORE the wind triangle, because TAS is an
 *     input to it.
 *   · {@link aircraftFuelValues} — Kraftstoff „Reise“, which is the cruise burn
 *     times the TOTAL FLIGHT TIME the route pass just computed. Runs after that
 *     pass and before the fuel block, so one recompute is enough and nothing
 *     feeds back into itself.
 */

import { AVGAS_KG_PER_L, cruiseSetting, fmtHM, isConfirmed, type AircraftProfile } from '@avia/core';

import { legField, LEG_NUMBERS, type LegNumber } from './fields';

/** The aircraft as the plan sees it: the record's identity plus its profile. */
export interface AircraftFeed {
  id: string;
  registration: string;
  /** `aircraft.type_code` — the fallback for the Muster cell when the profile has no ICAO type */
  typeCode: string | null;
  profile: AircraftProfile;
}

/** Which rows of the route block a profile may write a TAS into. */
export type RowFilled = (row: LegNumber | 'alt') => boolean;

/**
 * Which profile paths each fed cell is computed from.
 *
 * The plan has to keep the profile's two tiers apart (see `aircraftPresets.ts`): a figure
 * the pilot ticked off against THIS airframe's POH or weighing report is a measurement,
 * everything else — a preset's type-typical number, or one typed and not yet checked — is
 * an estimate. A cell is only as good as its weakest input, so a cell counts as confirmed
 * exactly when every path behind it does.
 *
 * `lfz_muster`, `lfz_kennz` and the two `mb_unit_*` cells are absent on purpose: they are
 * identity and units, not measurements, so there is nothing to confirm about them.
 */
const CELL_PATHS: Readonly<Record<string, readonly string[]>> = {
  fuel_anlassen_l: ['fuel.taxi_l'],
  fuel_vorrat_l: ['fuel.usable_l'],
  fuel_reserve_l: ['cruise.fuel_lph', 'fuel.reserve_min'],
  fuel_vorrat_zeit: ['cruise.fuel_lph', 'fuel.usable_l'],
  fuel_reise_l: ['cruise.fuel_lph'],
  mb_leer_m: ['mass.empty_kg'],
  mb_leer_mom: ['mass.empty_moment'],
  mb_kna_m: ['fuel.unusable_l'],
};
/** every `leg<N>_tas` and `alt_tas` — one cruise setting feeds them all */
const TAS_PATHS: readonly string[] = ['cruise.tas_kt'];

/**
 * Is this fed cell an ESTIMATE rather than a figure confirmed for this airframe?
 *
 * `mb_leer_m` seeded from a preset's `typical_empty_kg` is the case that matters: it is the
 * base of the whole P28–P32 sum that gets stamped onto the printed form, and it must not
 * look like a weighing-report number.
 */
export function isEstimated(p: AircraftProfile, id: string): boolean {
  const paths = id.endsWith('_tas') ? TAS_PATHS : CELL_PATHS[id];
  if (!paths) return false;
  return !paths.every(path => isConfirmed(p, path));
}

/** At most one decimal, no trailing ".0" — the way the paper form is written. */
export function fmtNum(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '';
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Litres the fuel block is written in: whole litres, as on the form. */
const fmtL = (v: number): string => String(Math.round(v));

/**
 * Everything the profile fills that does not depend on the computed flight time.
 *
 * A value the profile does not know is ABSENT from the map — never an empty
 * string — so an unknown never overwrites anything and never marks a cell as
 * "from the aircraft profile".
 */
export function aircraftStaticValues(a: AircraftFeed | null | undefined, rowFilled: RowFilled): Map<string, string> {
  const out = new Map<string, string>();
  if (!a) return out;
  const p = a.profile;

  const muster = (p.icao ?? a.typeCode ?? '').trim().toUpperCase();
  if (muster) out.set('lfz_muster', muster);
  const reg = a.registration.trim().toUpperCase();
  if (reg) out.set('lfz_kennz', reg);

  const cruise = cruiseSetting(p);
  const tas = cruise?.tas_kt ?? null;
  const lph = cruise?.fuel_lph ?? null;

  // TAS goes only into rows that carry a route: a plan with two legs must not
  // print a cruise speed into the ten empty rows below them.
  if (tas != null) {
    // the TAS cells are integer-kind on the form (`fields.ts` ROUTE_KINDS), so a
    // fractional cruise TAS has to be rounded here — writing "110.5" would make the
    // form flag its own fed value as invalid
    const v = String(Math.round(tas));
    for (const j of LEG_NUMBERS) if (rowFilled(j)) out.set(legField(j, 'tas'), v);
    // the alternate row is treated exactly like a leg row — its "checkpoint" is alt_name
    if (rowFilled('alt')) out.set('alt_tas', v);
  }

  const fuel = p.fuel;
  if (fuel?.taxi_l != null) out.set('fuel_anlassen_l', fmtL(fuel.taxi_l));
  if (fuel?.usable_l != null) out.set('fuel_vorrat_l', fmtNum(fuel.usable_l));
  if (lph != null && lph > 0 && fuel?.reserve_min != null) {
    out.set('fuel_reserve_l', fmtL((lph * fuel.reserve_min) / 60));
  }
  if (lph != null && lph > 0 && fuel?.usable_l != null) {
    const mins = Math.round((fuel.usable_l / lph) * 60);
    // the cell is HH:MM and the form's own validator stops at 23:59 — an
    // endurance longer than a day is not a general-aviation number anyway
    if (mins > 0 && mins < 24 * 60) out.set('fuel_vorrat_zeit', fmtHM(mins));
  }

  const mass = p.mass;
  if (mass?.empty_kg != null) out.set('mb_leer_m', fmtNum(mass.empty_kg));
  if (mass?.empty_moment != null) out.set('mb_leer_mom', fmtNum(mass.empty_moment));
  if (mass?.unit_mass) out.set('mb_unit_masse', mass.unit_mass);
  if (mass?.unit_moment) out.set('mb_unit_moment', mass.unit_moment);
  // Nicht ausfliegbarer Kraftstoff as a MASS: litres × 0.72 kg/l (avgas). Only
  // when the block is kept in kilograms — converting into "lbs." here would
  // silently mix units in a mass-and-balance sum.
  if (fuel?.unusable_l != null && mass?.unit_mass !== 'lbs.') {
    out.set('mb_kna_m', fmtNum(fuel.unusable_l * AVGAS_KG_PER_L));
  }

  return out;
}

/**
 * Kraftstoff „Reise“: the cruise burn over the total flight time the route pass
 * computed (or the one the pilot wrote into P18 — the store passes the effective
 * value, exactly what ETA is built from).
 */
export function aircraftFuelValues(
  a: AircraftFeed | null | undefined,
  totalTimeMin: number | null,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!a || totalTimeMin == null || !Number.isFinite(totalTimeMin) || totalTimeMin <= 0) return out;
  const lph = cruiseSetting(a.profile)?.fuel_lph ?? null;
  if (lph == null || lph <= 0) return out;
  out.set('fuel_reise_l', fmtL((lph * totalTimeMin) / 60));
  return out;
}

/** A short "TAS 110 kt · 32 l/h · 201 l · MTOM 1157 kg" line for the aircraft list. */
export interface ProfileSummary {
  tas: string;
  lph: string;
  usable: string;
  mtow: string;
  seats: string;
}

export function profileSummary(p: AircraftProfile): ProfileSummary {
  const c = cruiseSetting(p);
  return {
    tas: fmtNum(c?.tas_kt),
    lph: fmtNum(c?.fuel_lph),
    usable: fmtNum(p.fuel?.usable_l),
    mtow: fmtNum(p.mass?.mtow_kg),
    seats: fmtNum(p.seats),
  };
}
